// src/context/UserContext.tsx
import { notify } from "@/components/common/Notification";
import { saveGluceraAuthSession } from "@/lib/authSession";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalTenantId,
  setLocalUserInfo,
} from "@/lib/storeManager";
import { AuthService, UserPreferencesService } from "@/services/APIs";
import {
  AUTH_SESSION_UPDATED,
  setupProactiveTokenRefresh,
  type TokenPair,
} from "@/services/tokenRefresh";
import { shouldFetchAuthenticatedBootstrapApis } from "@/lib/authBootstrapGate";
import { AuthRes, SocialLoginRes } from "@/utils/types/auth";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

interface DecodedToken {
  userId: string;
  email: string;
  exp: number;
  [key: string]: any;
}

interface UserContextType {
  jwtToken: string | null;
  refreshToken: string | null;
  decodedToken: DecodedToken | null;
  isAuthenticated: boolean;
  preferencesData: any; // Added preferences data
  fetchPreferencesData: () => Promise<void>; // Function to fetch preferences data
  signup: (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ) => Promise<any>;
  login: (email: string, password: string) => Promise<AuthRes | undefined>;
  logout: () => Promise<any>;
  socialLogin: (provider: string) => Promise<SocialLoginRes | undefined>;
  forgotPassword: (email: string) => Promise<any>;
  forgotPasswordReset: (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => Promise<any>;
  resetPassword: (values: {
    current_password: string;
    new_password: string;
  }) => Promise<any>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const applyTokensToState = (
  tokens: TokenPair,
  setters: {
    setJwtToken: (v: string) => void;
    setRefreshToken: (v: string) => void;
    setDecodedToken: (v: DecodedToken) => void;
  }
) => {
  setters.setJwtToken(tokens.access);
  setters.setRefreshToken(tokens.refresh);
  setters.setDecodedToken(jwtDecode<DecodedToken>(tokens.access));
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [preferencesData, setPreferencesData] = useState<any>(null);

  const syncStateFromStorage = useCallback(() => {
    const access = getAccessToken();
    const refresh = getRefreshToken();
    if (!access || !refresh) return;
    try {
      applyTokensToState(
        { access, refresh },
        { setJwtToken, setRefreshToken, setDecodedToken }
      );
    } catch {
      // Access token couldn't be decoded (e.g. stale/corrupt after a reload).
      // Do NOT wipe the whole session — the refresh token is still valid and
      // the proactive refresh / 401 interceptor will fetch a fresh access
      // token. Wiping here logged users out on every reload with a bad token.
      console.warn(
        "⚠️ Stored access token could not be decoded; keeping session and awaiting refresh."
      );
      setRefreshToken(refresh);
    }
  }, []);

  useEffect(() => {
    syncStateFromStorage();
  }, [syncStateFromStorage]);

  const fetchPreferencesData = useCallback(async () => {
    if (!shouldFetchAuthenticatedBootstrapApis()) return;
    const data = await UserPreferencesService.getUserPreferencesGrid();
    if (data) {
      setPreferencesData(data);
    }
  }, []);

  useEffect(() => {
    if (!shouldFetchAuthenticatedBootstrapApis()) return;
    void fetchPreferencesData();
  }, [fetchPreferencesData]);

  const isAuthenticated = Boolean(jwtToken && refreshToken);

  const onTokensUpdated = useCallback(
    (tokens: TokenPair) => {
      applyTokensToState(tokens, {
        setJwtToken,
        setRefreshToken,
        setDecodedToken,
      });
    },
    []
  );

  useEffect(() => {
    return setupProactiveTokenRefresh(onTokensUpdated);
  }, [onTokensUpdated]);

  useEffect(() => {
    const onSessionUpdated = (event: Event) => {
      const tokens = (event as CustomEvent<TokenPair>).detail;
      if (tokens?.access) {
        onTokensUpdated(tokens);
        if (shouldFetchAuthenticatedBootstrapApis()) {
          void fetchPreferencesData();
        }
      }
    };
    window.addEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);
    return () =>
      window.removeEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);
  }, [onTokensUpdated, fetchPreferencesData]);

  const persistLoginSession = (
    access: string,
    refresh: string,
    user: AuthRes["user"]
  ) => {
    saveGluceraAuthSession({ access, refresh }, user);
    applyTokensToState(
      { access, refresh },
      { setJwtToken, setRefreshToken, setDecodedToken }
    );
    setLocalUserInfo({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      user_type: user.user_type,
    });
  };

  const handleSignup = async (
    userEmail: string,
    userPassword: string,
    userFirstName: string,
    userLastName: string
  ) => {
    const response = await AuthService.signup({
      email: userEmail,
      password: userPassword,
      first_name: userFirstName,
      last_name: userLastName,
    });
    type TokenPayload = { access: string; refresh: string };
    const payload = (response?.data ?? response) as TokenPayload;
    const access = payload?.access;
    const refresh = payload?.refresh;

    if (response && access && refresh) {
      persistLoginSession(access, refresh, response.user);
      fetchPreferencesData();
      return response;
    }
  };

  const handleLogin = async (userEmail: string, userPassword: string) => {
    const response = await AuthService.login({
      email: userEmail,
      password: userPassword,
    });
    if (response?.access && response?.refresh) {
      persistLoginSession(response.access, response.refresh, response.user);
      fetchPreferencesData();
      return response;
    }
  };

  const handleForgotPassword = async (userEmail: string) => {
    const response = await AuthService.forgotPassword(userEmail);
    if (response) {
      notify.success("Reset password email sent successfully");
      return response;
    }
  };

  const handleForgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => {
    const response = await AuthService.forgotPasswordReset(id, payload);
    if (response) {
      notify.success("Password reset successfully");
      return response;
    }
  };

  const handleLogout = async () => {
    try {
      const response = await AuthService.logout();
      setJwtToken(null);
      setRefreshToken(null);
      setDecodedToken(null);
      const preservedTenant = getTenantId();
      clearLocalStorage();
      window.location.reload();
      if (preservedTenant !== null) {
        setLocalTenantId(preservedTenant);
      }
      return response;
    } catch {
      const preservedTenant = getTenantId();
      clearLocalStorage();
      window.location.reload();
      if (preservedTenant !== null) {
        setLocalTenantId(preservedTenant);
      }
    }
  };

  const handleResetPassword = async (values: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await AuthService.resetPassword(values);
    return response;
  };

  const handleSocialLogin = async (provider: string) => {
    const response = await AuthService.socialLogin(provider);
    return response;
  };

  return (
    <UserContext.Provider
      value={{
        jwtToken,
        refreshToken,
        decodedToken,
        isAuthenticated,
        preferencesData,
        fetchPreferencesData,
        signup: handleSignup,
        login: handleLogin,
        logout: handleLogout,
        socialLogin: handleSocialLogin,
        forgotPassword: handleForgotPassword,
        forgotPasswordReset: handleForgotPasswordReset,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext };
